import useCalendlyLink from '@/routes/hooks/useCalendlyLink';
import { useCase } from '@/routes/hooks/useCase';
import isConsultationFromStatic from '@/utils/isConsultationFromStatic';
import { getStepName } from '@/utils/stepUtils';
import { useMemo } from 'react';

const DEFAULT_CONSULTATION_LINK = 'https://calendly.com/d/csys-mpw-s5p/resolve-free-consult';

type ConsultationLinkProps = {
  stepId?: string;
};

type ConsultationLinkResult = {
  consultationLink: string;
  isLoading: boolean;
};

export const useConsultationLink = ({
  stepId = '',
}: ConsultationLinkProps): ConsultationLinkResult => {
  const stepName = getStepName(stepId);
  const case_ = useCase();

  // Determine if this step requires a special consultation link from the backend
  const isSpecialConsultationLink = useMemo(
    () => (stepName ? isConsultationFromStatic({ stepName }) : false),
    [stepName],
  );

  // Always call the hook, but only enable the query when needed
  const { calendlyLink, isLoading } = useCalendlyLink({
    caseId: case_?.id ?? '',
    stepId,
    enabled: isSpecialConsultationLink && !!stepName,
  });

  // If stepId is not provided or invalid, return the default link
  if (!stepName) {
    return { consultationLink: DEFAULT_CONSULTATION_LINK, isLoading: false };
  }

  // For special consultation links, use the fetched link with fallback to default
  // For regular steps, always use the default link
  if (isSpecialConsultationLink) {
    return {
      consultationLink: calendlyLink ?? DEFAULT_CONSULTATION_LINK,
      isLoading,
    };
  }

  return { consultationLink: DEFAULT_CONSULTATION_LINK, isLoading: false };
};
